import React, { Dispatch, SetStateAction, useContext, useState } from 'react'
import { cloneDeep, isEqual } from 'lodash'
import { Trans, t } from '@lingui/macro'

import { DangerouslySetInnerHTML } from 'front-end/src/utils/DangerouslySetInnerHTML'

import {
  formatTextToHtml,
  SurveyAnswerType,
  SurveyQuestionTypes
} from '../../../../../shared/surveys'
import { linkBuilder, staticLinks } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { Box, ButtonClean, ButtonPrimary } from 'front-end/src/components'
import { notification } from '../../../components/common/notifications/Notifications'
import { ISurveyQuestion } from '../types'
import BoolQuestion from './QuestionTypeElements/BoolQuestion'
import { ChooseMultipleQuestion } from './QuestionTypeElements/ChooseMultipleQuestion'
import ChooseOneQuestion from './QuestionTypeElements/ChooseOneQuestion'
import RatingQuestion from './QuestionTypeElements/RatingQuestion'
import TextQuestion from './QuestionTypeElements/TextQuestion'
import { StudentSurveyContext } from './StudentSurvey'
import HeadTitle from '../../../components/common/head/HeadTitle'
import { useSaveSurveyAnswerMutation } from './StudentSurveyQuestion.codegen'
import { filterInfoQuestions, getIndexOfNonInfoQuestion } from '../helpers'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'

interface IStudentSurveyQuestionProps {
  question: ISurveyQuestion
  indexOfPage: number
  countOfPages: number
  setActivePage: (number) => void
  isPreviewMode: boolean
  setQuestions: Dispatch<SetStateAction<ISurveyQuestion[]>>
  questions: ISurveyQuestion[]
}

export interface IStudentSurveyQuestionTypeProps {
  question: ISurveyQuestion
  setAnswer: Dispatch<SetStateAction<SurveyAnswerType>>
  answer: SurveyAnswerType
}

const StudentSurveyQuestion = ({
  question,
  questions,
  indexOfPage,
  countOfPages,
  setActivePage,
  isPreviewMode,
  setQuestions
}: IStudentSurveyQuestionProps) => {
  const defaultAnswer = isPreviewMode ? null : question.userAnswer?.value
  const { survey } = useContext(StudentSurveyContext)
  const [answer, setAnswer] = useState<SurveyAnswerType>(defaultAnswer)
  const [updateAnswer] = useSaveSurveyAnswerMutation()
  const countOfQuestions = filterInfoQuestions(questions).length
  const indexOfQuestion = getIndexOfNonInfoQuestion(questions, indexOfPage)

  const questionProps = {
    setAnswer,
    question,
    answer
  }

  const renderStudentSurveyQuestionContent = (question: ISurveyQuestion) => {
    switch (question.type) {
      case SurveyQuestionTypes.BOOL:
        return <BoolQuestion {...questionProps} />
      case SurveyQuestionTypes.CHOOSE_ONE:
        return <ChooseOneQuestion {...questionProps} />
      case SurveyQuestionTypes.MULTIPLE_CHOICE:
        if (answer && !Array.isArray(answer)) {
          throw new TypeError('MULTIPLE_CHOICE must have an array as answer')
        }
        return (
          <ChooseMultipleQuestion
            answer={answer as string[] | null}
            setAnswer={setAnswer}
            question={question}
          />
        )
      case SurveyQuestionTypes.RATING:
        return <RatingQuestion {...questionProps} />
      case SurveyQuestionTypes.TEXT:
        return <TextQuestion {...questionProps} />
      default:
        return null
    }
  }

  const sendingAnswer = shouldAnswerBeSend({
    questionType: question.type,
    storedAnswer: question.userAnswer?.value,
    newAnswer: answer,
    isPreviewMode
  })

  const isLastQuestion = () => indexOfPage === countOfPages - 1

  const questionTitle = question.title && formatTextToHtml(question.title)

  return (
    <Box className="StudentSurvey__question">
      <Box as="fieldset" display="flex" flexDirection="column">
        <HeadTitle title={survey?.title} />
        {question.type !== SurveyQuestionTypes.INFO && (
          <Box
            as={question.type === SurveyQuestionTypes.TEXT ? 'label' : 'legend'}
            /* @ts-ignore */
            for={`question-id-${question.id}`}
            className="StudentSurvey__question__title"
            dangerouslySetInnerHTML={{ __html: questionTitle }}
          />
        )}
        <div className="StudentSurvey__question__order">
          {question.type == SurveyQuestionTypes.INFO ? (
            <Trans>Information</Trans>
          ) : (
            <Trans>
              Question {indexOfQuestion + 1}/{countOfQuestions}
            </Trans>
          )}
        </div>
        <Box
          css={getQuillStyle('ql-editor')}
          className="StudentSurvey__question__description ql-editor"
        >
          <DangerouslySetInnerHTML content={question.description || ''} />
        </Box>
        <div className="StudentSurvey__question__content">
          {renderStudentSurveyQuestionContent(question)}
        </div>
        <div className="StudentSurvey__question__controls">
          {indexOfPage > 0 && (
            <ButtonClean onClick={() => setActivePage(indexOfPage - 1)}>
              &lt; <Trans>Back</Trans>
            </ButtonClean>
          )}
          <ButtonPrimary
            onClick={async () => {
              if (!isAnswerValid(question?.type, answer)) {
                notification.warning(t`Please answer this question`)
                return
              }

              if (sendingAnswer) {
                const { data } = await updateAnswer({
                  variables: {
                    questionId: question.id,
                    input: processAnswer(question.type, answer),
                    surveyId: survey.id
                  }
                })

                if (data?.surveyQuestion?.saveAnswer) {
                  const newQuestions = cloneDeep(questions)

                  newQuestions[indexOfPage].userAnswer =
                    data.surveyQuestion.saveAnswer
                  setQuestions(newQuestions)
                  if (isLastQuestion()) {
                    notification.info(t`Submission successful`)
                    navigateTo(staticLinks.home)
                    return
                  }
                } else {
                  notification.error(t`Unable to save the answer`)
                  return
                }
              }
              if (isLastQuestion()) {
                navigateTo(linkBuilder.surveyDetailManage(survey.id, 'create'))
                return
              }

              setActivePage(indexOfPage + 1)
            }}
          >
            {isLastQuestion() ? t`Finish survey` : t`Continue`}
          </ButtonPrimary>
        </div>
      </Box>
    </Box>
  )
}

export function processAnswer(
  questionType: SurveyQuestionTypes | null | undefined,
  answer: any
) {
  if (
    questionType === SurveyQuestionTypes.TEXT ||
    questionType === SurveyQuestionTypes.INFO
  ) {
    return answer || ''
  }

  return answer
}

export function isAnswerValid(
  questionType: SurveyQuestionTypes | null | undefined,
  answer: any
) {
  if (
    questionType === SurveyQuestionTypes.INFO ||
    questionType === SurveyQuestionTypes.TEXT
  ) {
    return true
  }

  return answer !== null && answer !== undefined
}

export function shouldAnswerBeSend({
  questionType,
  isPreviewMode,
  storedAnswer,
  newAnswer
}: {
  questionType: SurveyQuestionTypes | null | undefined
  isPreviewMode: boolean
  storedAnswer: any
  newAnswer: any
}) {
  if (isPreviewMode) {
    return false
  }

  if (questionType === SurveyQuestionTypes.INFO) {
    return true
  }

  // no answer -> we still should store placeholder answer
  // otherwise survey never going to get to finish state
  if (questionType === SurveyQuestionTypes.TEXT && !newAnswer) {
    return true
  }

  return !isEqual(storedAnswer, newAnswer)
}

export default StudentSurveyQuestion
